/* eslint-disable  @typescript-eslint/no-explicit-any */
import { BehaviorSubject, filter, firstValueFrom, map, Observable, Subject, tap } from 'rxjs';
import type { FilterRequest } from '../../../models/cose-api/request/query-body-request.model';
import type { FacetResponse, FacetsOptionsResponse } from '../../../models/cose-api/response/facet-options.model';
import { FILTERS_ID_SEPARATOR } from '../../../constants/filterConstants';
import { mergeDeepFilters } from '../../helpers/merge-deep';
import { FacetTypeEnum, type FacetValue, type NestedFilter } from '../../../api/model/response/facets/Facets';
import type { EventEmitter } from '@stencil/core';
import type { FacetChange } from '../../../components/result-page/facets/FacetChange';
import type { FacetChildUiModel, FacetTreeUiModel } from '../../../models/ui/facets/facet-tree-ui.model';
import { cloneObjectWithNoReference } from '../../helpers/clone-object-with-no-reference';

export interface FilterChildRequestUnique {
    value: string;
    uniqueId: string;
    count?: number;
    selected?: boolean;
    children: FilterChildRequestUnique[];
}

export interface FilterRequestUnique {
    id: string;
    uniqueId: string;
    count?: number;
    selected?: boolean;
    children: FilterChildRequestUnique[];
}

export class FacetsService {
  key: string;
  facetsState$: BehaviorSubject<FilterRequestUnique[]> = new BehaviorSubject([]);
  facetsRequestState$: BehaviorSubject<FilterRequest[]> = new BehaviorSubject([]);
  facetsOptions$: BehaviorSubject<FacetsOptionsResponse>;
  facetChipsState$: BehaviorSubject<FacetValue[]> = new BehaviorSubject([]);
  onClearFacet$: Subject<FacetValue> = new Subject<FacetValue>();
  private static instancesMap: Map<string,FacetsService> = new Map();

  constructor() {
      this.facetsState$ = new BehaviorSubject([]);
      this.facetsOptions$ = new BehaviorSubject(null);
  }

  get facetsState(): Observable<any[]> {
      return this.facetsState$.asObservable();
  }

  get facetsRequestState(): Observable<any[]> {
      return this.facetsRequestState$.asObservable();
  }

  get facetsOptions(): Observable<FacetsOptionsResponse> {
      return this.facetsOptions$.asObservable();
  }

  get facets(): Observable<FacetResponse[]> {
      return this.facetsOptions$
        .pipe(
          filter((fOpt) => !!fOpt),
          map((fOpt:FacetsOptionsResponse) => {
              return fOpt.facets || [];
          }),
        );
  }

  get facetsChipsState(): Observable<FacetValue[]> {
      return this.facetChipsState$;
  }

  get onClearFacet(): Observable<FacetValue> {
      return this.onClearFacet$;
  }

  get classRef(): typeof FacetsService {
      return FacetsService;
  }

  setFacetsState(facets: FilterRequestUnique[]) {
      this.facetsState$.next(facets);
  }

  setFacetsRequestState(facets: FilterRequestUnique[]) {
      let clearFacets = [];
      if(facets.length > 0){
        clearFacets = FacetsService.clearTreeUnique(facets);
      }
      this.facetsRequestState$.next(clearFacets);
  }

  setFacets(facets: FacetsOptionsResponse) {
      this.facetsOptions$.next(facets);
  }

  setFacetsChipsState(facetValues: FacetValue[]) {
      this.facetChipsState$.next(facetValues);
  }

  clearFacet(facetValue: FacetValue) {
      this.onClearFacet$.next(facetValue);
  }

  //if selected=true - add to state, if selected=false - remove from state
  addOrRemoveChipOnState(facetValue: FacetValue, selected: boolean): void {
      if(facetValue.uniqueId) {
        const currentFacetChipsState = this.facetChipsState$.value;
        if(selected) {
          const isFacetChipsExists = currentFacetChipsState.some((chips)=> chips.uniqueId === facetValue.uniqueId);
          if(!isFacetChipsExists) {
            currentFacetChipsState.push(facetValue);
          }
        } else {
          const chipsIndex = currentFacetChipsState.findIndex((chips)=> chips.uniqueId === facetValue.uniqueId);
          if(chipsIndex !== -1) {
            currentFacetChipsState.splice(chipsIndex, 1);
          }
        }

        this.facetChipsState$.next(currentFacetChipsState);
      }
  }

  addChipsToState(facetValues: FacetValue[]): void {
      facetValues.forEach(facetValue => this.addOrRemoveChipOnState(facetValue, true));
  }

  async createUpdateChipsStateFromResponseTree(tree): Promise<void> {
      const facetsChipsState = await firstValueFrom(this.facetsChipsState);
      const facetsRequestTreeUnique = FacetsService.setUnique([...tree]);

      facetsRequestTreeUnique.forEach((facet) => {
          this.findFilterUpdateChipsState(facet.children, facetsChipsState);
      });
  }

  findFilterUpdateChipsState(filters, facetsChipsState) {
      filters.forEach(filter => {
          if(filter.selected && !facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              fieldValue: filter.value,
              count: filter.count
            }, true);
          } else if(!filter.selected && facetsChipsState.some(f => f.uniqueId === filter.uniqueId)) {
            this.addOrRemoveChipOnState({
              uniqueId: filter.uniqueId,
              displayName: filter.displayName,
              fieldValue: filter.value,
              count: filter.count
            }, false);
          }

          if(filter.children.length > 0) {
            this.findFilterUpdateChipsState(filter.children, facetsChipsState);
          }
      });
  }

  onFacetChange(facetValue: FacetChildUiModel, facetsState, facets, searchFacet = false) {
      const tree: FilterRequest[] | FilterRequestUnique[] = facetsState;

      if (facets && facetValue.selected) {
        let branch: FilterRequest | FilterRequestUnique | any = {};

        facets.forEach((facet: FacetTreeUiModel) => {
            const facetId = facet.uniqueId.split(FILTERS_ID_SEPARATOR)[0] || facet.id;
            const root = [{ uniqueId: facet.uniqueId, valueId: facetId }];

            const filters: Array<{ uniqueId: string, valueId: string }>
                = FacetsService.findFacet(facet.children, facetValue['uniqueId'], facet.uniqueId, facet.id, root);

            const facetRoot = {
              id: filters[0].valueId || facetId,
              uniqueId: filters[0].uniqueId,
              children: []
            };

            const valuesFilters: Array<{ uniqueId: string, valueId: string }> = filters.slice(facetId ? 2 : 1, filters.length);

            // adjustment so that if uniqueId consists of three elements separated by ":", the array is built correctly
            // eslint-disable-next-line no-unsafe-optional-chaining
            if(valuesFilters[0]?.uniqueId && (valuesFilters[0]?.uniqueId).split(FILTERS_ID_SEPARATOR).length === 3) {
              const splittedUniqueId = (valuesFilters[0].uniqueId).split(FILTERS_ID_SEPARATOR);
              const parentFilterValue = {
                uniqueId: splittedUniqueId[0] + FILTERS_ID_SEPARATOR + splittedUniqueId[1],
                valueId: splittedUniqueId[1]
              };
              valuesFilters.unshift(parentFilterValue);
              facetRoot.id = facet.id;
            }

            if (searchFacet) {
              valuesFilters.push({ uniqueId: facetId + FILTERS_ID_SEPARATOR + facetValue.value, valueId: facetValue.value });
            }
            if (facetRoot.id === facet.id && filters.length > 1) {
              const valueTo = FacetsService.arrayToTree(valuesFilters, facetRoot);
              branch = mergeDeepFilters(branch, valueTo);
            }
        });

        if (tree.some(v => v.id === branch['id'])) {
          const index = tree.findIndex(v => v.id === branch['id']);
          if (searchFacet) {
            tree[index].children[0].value = facetValue.value;
          } else {
            tree[index] = mergeDeepFilters(tree[index], { ...branch });
          }
        } else {
          tree.push({ ...branch });
        }
      } else {
        tree.forEach((facet, i) => {
            FacetsService.findFacetAndDelete(tree[i].children, facetValue.uniqueId, false);
            if (tree[i].children.length === 0) {
              tree.splice(i, 1);
            }
        });
      }

      this.addOrRemoveChipOnState(facetValue as unknown as FacetValue, facetValue.selected);
      this.setFacetsState(tree as FilterRequestUnique[]);
      this.setFacetsRequestState(cloneObjectWithNoReference<any>(tree));
  }

  static findFacet(fcs, checkValueUnique, parentUniqueId, parentValue, root, additionalData?, isFinded = false) {
      if(isFinded) {
        return root;
      }
      if(fcs.length > 0) {
        fcs.forEach((f) => {
            let {value, uniqueId} = f;
            if(additionalData && additionalData?.priceKey) {
              const priceValues = createRootForPriceFilter(f, root, checkValueUnique, additionalData, parentUniqueId, parentValue);

              // Range Price Filter root needs to have 2 filter values (start and end value) as a childs of one parent. By default, values are nested.
              root = priceValues.root;

              value = priceValues.f['value'];
              uniqueId = priceValues.f['uniqueId'];
            } else if(uniqueId === checkValueUnique){
              root = [...root,...[{uniqueId: parentUniqueId, valueId: parentValue}],...[{uniqueId: uniqueId, valueId: value}]];
              isFinded = true;
            } else{
              root = FacetsService.findFacet(f.children, checkValueUnique, uniqueId, value, root, additionalData, isFinded);
            }
        });
      }
      return root;
  };

  static findFacetAndDelete(fcs, checkValueUnique, isFinded) {
      if(isFinded) {
        return;
      }
      if(fcs.length > 0) {
        fcs.forEach((f,i) => {
            if(f['uniqueId'] === checkValueUnique){
              fcs.splice(i,1);
              isFinded = true;
            } else{
              FacetsService.findFacetAndDelete(f.children, checkValueUnique, isFinded);
            }
        });
      }
      return;
  };

  static arrayToTree(
    values: Array<{uniqueId:string,valueId:string}>,
    facetRoot:{id:string, children:any[]},  //eslint-disable-line
    additionalData?: object
  ) {
      let v;
      const changeMakeChild = (part, value) => {
          part.children.push({uniqueId:value.uniqueId, value: value.valueId, children:[]});
          return part.children[0];
      };
      values.forEach((value: {uniqueId:string,valueId:string}, i) => {
          if(i === 0) {
            v = changeMakeChild(facetRoot, value);
          } else{
            if(additionalData?.['priceKey'] && value.uniqueId.startsWith(additionalData?.['priceKey'])) {
              v = changeMakeChild(facetRoot, value);
            } else {
              v = changeMakeChild(v, value);
            }
          }
      });
      return facetRoot;
  };

  static setUnique(facets, parent = '', i = 0) {
      i++;
      Object.keys(facets).forEach((key) => {
          const inName = parent + ((i === 1) ? '' : FILTERS_ID_SEPARATOR) + facets[key][(i === 1) ? 'id' : 'value'];

          if(facets[key]['uniqueId'] !== inName) {
            if (facets[key]['uniqueId'] && i !== 1) {
              facets[key]['uniqueId'] += inName;
            } else {
              facets[key]['uniqueId'] = inName;
            }
          }

          if(facets[key]['children'] && facets[key]['children'].length > 0) {
            facets[key]['children'] = FacetsService.setUnique(facets[key]['children'], facets[key]['uniqueId'], i);
            if(facets[key]['fieldValues'] && facets[key]['fieldValues'].length > 0) {
              facets[key]['fieldValues'] = FacetsService.setUnique(facets[key]['fieldValues'], facets[key]['uniqueId'], i);
            }
          }
      });

      return facets;
  }

  static clearTreeUnique(tree: Array<FilterRequestUnique|FilterChildRequestUnique>): Array<FilterRequestUnique|FilterChildRequestUnique|FilterRequest> {
      tree.map((f) => {
          if(f.uniqueId) {
            delete f.uniqueId;
          }
          if(f.children && f.children.length > 0) {
            f.children = FacetsService.clearTreeUnique(f.children) as Array<FilterChildRequestUnique>;
          }
          return f;
      });

      return tree as FilterRequest[];
  }

  static findFacetByName(
    name: string,
    facetsChildren: FilterChildRequestUnique[],
    idOrValue: string,
    query: FilterRequestUnique | {} = {},
    chipsList: FacetValue[] = []
  ): {currentQuery: FilterRequestUnique | {}, currentChips: FacetValue[]} {
      let wasFind = false;
      facetsChildren.forEach((facetsChild)=> {
          if(facetsChild.value === name && (query as FilterRequestUnique)?.children) {
            (query as FilterRequestUnique).children.push({value: facetsChild.value, uniqueId: facetsChild.uniqueId, children:[]});
            chipsList.push({
              fieldValue: facetsChild.value,
              displayName: facetsChild.value,
              selected: facetsChild.selected,
              count: facetsChild.count,
              uniqueId: facetsChild.uniqueId
            });
            wasFind = true;
          }
          if(facetsChild.children && !wasFind && query) {
            const {currentQuery, currentChips} = FacetsService.findFacetByName(name, facetsChild.children, facetsChild.value, query, chipsList);
            query = currentQuery;
            chipsList = [...chipsList, ...currentChips];
          }
      });

      return {currentQuery: query, currentChips: chipsList};
  }

  static prepareQueryByNames(names: string[], facets: FilterRequestUnique[], nestedFilters?: Array<NestedFilter>): {currentQueries: FilterRequestUnique[], currentChips: FacetValue[]} {
        const queries: any[] = []; //eslint-disable-line
      let chips: FacetValue[] = [];
      names.forEach((name: string) => {
          facets.forEach((facet) => {
              if(facet.children) {
                const {currentQuery, currentChips} = FacetsService.findFacetByName(name, facet.children, facet.id,{id: facet.id, uniqueId: facet.uniqueId, children: []});
                chips = [...chips, ...currentChips];
                queries.push(currentQuery);
              }
          });
      });

      const filteredQueries: FilterRequestUnique[] = queries.filter((q)=>q.children.length > 0);

      return {currentQueries: FacetsService.mergeArrayQueries(filteredQueries, nestedFilters), currentChips: chips};
  }

  static mergeArrayQueries(filteredQueries: FilterRequestUnique[], nestedFilters?: Array<any>): FilterRequestUnique[] {
      const newQ = [];
      filteredQueries.forEach((q) => {
          const index = newQ.findIndex(nq => nq.uniqueId === q.uniqueId);
          if(index === -1) {
            newQ.push(q);
          } else {
            newQ[index] = mergeDeepFilters(newQ[index], q);
          }
      });

      // This is a case to handle nested filters in the request payload.
      if(nestedFilters?.length > 0) {
        nestedFilters.forEach((nestedFilter) => {
            if(nestedFilter?.id && nestedFilter?.value) {

              if(newQ[0]?.id == nestedFilter.id && newQ[0].children?.[0]?.value !== nestedFilter.value) {

                // To provide the correct structure of the request payload for nested filters, 
                // the nested filter must be added as a child of the parent filter.
                newQ[0].children = [{value: nestedFilter.value, children: [...newQ[0].children]}];
              }
            }
        });
      }
      return newQ;
  }

  static callfacetChangedEventByRequestArray(emiter: EventEmitter<FacetChange>, query: FilterRequestUnique[], blockQueryCall = true) {
      query.forEach((facet) => {
          facet.children.forEach((filter) => {
              emiter.emit({
                facetType: '',
                facetName: filter.value,
                facetValue: filter.value,
                facetCount: 0,
                facetQuery: '',
                value: filter.value,
                action: 'add',
                id: facet.id,
                uniqueId: filter.uniqueId,
                blockQueryCall
              });
          });
      });
  }

  static getSetInstance(key: string) {
      const instance = new this();
      instance.key = key;
      FacetsService.instancesMap.set(key, instance);
      return instance;
  }

  static getInstance(key: string): FacetsService {
      return FacetsService.instancesMap.has(key) ? FacetsService.instancesMap.get(key) : new this();
  }
}
function createRootForPriceFilter(f, root, checkValueUnique, additionalData, parentUniqueId, parentValue) {
    const {type} = f;
    let { value, uniqueId} = f;
    const {priceKey, toValue} = additionalData;

    if(type === FacetTypeEnum.RangeStart) {
      value = checkValueUnique.split(priceKey)[1];
      uniqueId = checkValueUnique;
      root = [...root,...[{uniqueId: parentUniqueId, valueId: parentValue}],...[{uniqueId: uniqueId, valueId: value}]];
    } else if(type === FacetTypeEnum.RangeEnd) {
      root = [...root,...[{uniqueId: priceKey + toValue, valueId: toValue}]];
    }
    return {root, f};
}

